/**
 * Configuración de planes para BioTemplatePremium.
 *
 * Soporta monedas/gateways:
 *   ARS → Mercado Pago Bricks  (suscripción mensual) — solo Argentina
 *   USD → Stripe Elements      (suscripción mensual) — resto del mundo fuera de Eurozona
 *   EUR → Stripe               (suscripción mensual) — Eurozona (ver `internationalStripeCurrency`)
 *
 * El país y el idioma vienen del Cloudflare Worker (window.__TEMPLATE_CONFIG__).
 * Los precios, videos y background son configurables via env vars (Vercel); fallbacks en este archivo.
 *
 * Idiomas soportados: español (es), inglés (en), portugués (pt), francés (fr), alemán (de), italiano (it).
 * Cualquier otro idioma hace fallback a inglés.
 *
 * ── Precios ──────────────────────────────────────────────────────────────────
 *   VITE_PREMIUM_ARS_STANDARD_PRICE    (ej. 29999)
 *   VITE_PREMIUM_ARS_VIP_PRICE         (ej. 49999)
 *   VITE_PREMIUM_USD_STANDARD_PRICE    (default 9.99 — Standard Member Stripe)
 *   VITE_PREMIUM_USD_VIP_PRICE         (default 29.99 — Exclusive Member Stripe)
 *   VITE_PREMIUM_EUR_STANDARD_PRICE    (opcional; si falta: USD standard × paridad)
 *   VITE_PREMIUM_EUR_VIP_PRICE         (opcional; si falta: USD VIP × paridad)
 *   VITE_PREMIUM_USD_EUR_PARITY        (opcional, ej. 0.92 — multiplicador USD→EUR para defaults)
 *
 * ── Analytics de funnel ─────────────────────────────────────────────────────
 *   `getPremiumPlansFunnelSnapshot(currency, language)` — precios/flags + `snapshot_version`
 *   para PostHog, Slack y correlación `invite_session_id` / `flow_trace_id`.
 *
 * ── Videos Mux (playback IDs) ────────────────────────────────────────────────
 *   Solo **VIP** monta `MuxVideoPlayerWithCTA` (BioTemplatePremium / Simple). No hay video Mux del plan Standard en esos templates.
 *   VITE_PREMIUM_MUX_ES_VIP / EN_VIP — obligatorias en prod (sin ID en código); si falta una, se usa la otra como respaldo de idioma
 *   Optional: VITE_SPECIAL_OFFER_PLAYBACK_ID cuando Special Offer está activo (reemplaza VIP)
 *
 * ── Background Image ─────────────────────────────────────────────────────────
 *   Bio premium **full**: `VITE_PREMIUM_BACKGROUND_IMAGE_URL` (+ mobile legacy en componente si aplica).
 *   Bio **Simple** e `/invitacion-premium`: ver `src/config/premiumSimpleBackgroundUrls.ts` (checklist prod de cuatro URLs + legacy).
 *
 * ── Discount Rate ────────────────────────────────────────────────────────────
 *   VITE_PREMIUM_VIP_DISCOUNT_RATE     Porcentaje de descuento VIP (0.10 = 10%)
 *                                      Default: 0.10 (10% off)
 *
 * ── Special Offer (Promociones Temporales) ───────────────────────────────────
 *   VITE_PREMIUM_SPECIAL_OFFER         Activar promoción especial (tipo Black Friday)
 *                                      - "true" o "1" o "active": Descuento visible desde inicio
 *                                      - cualquier otro valor o vacío: Modal al cerrar video (normal)
 *                                      Ejemplo: Para Black Friday, setear a "true"
 *
 *   VITE_SPECIAL_OFFER_PLAYBACK_ID     Video Mux específico para el Special Offer
 *                                      Reemplaza el video VIP durante la promoción
 */

import type {
  BioTemplatePremiumPlan,
  BioTemplatePremiumVipPlan,
} from "@/components/templates/BioTemplatePremium";
import { isVipPlanCardAvailable } from "@/config/vipConfig";
import { coalesceMuxPlaybackId } from "@/lib/canonicalMuxPlayback";
import {
  CANONICAL_STANDARD_PRICE_USD,
  CANONICAL_VIP_PRICE_USD,
} from "@/lib/canonicalVipMembership";

// ─── Helpers internos ─────────────────────────────────────────────────────────

function parsePrice(envVal: string | undefined, defaultVal: number): number {
  if (!envVal) return defaultVal;
  const n = Number(String(envVal).trim());
  return Number.isFinite(n) && n > 0 ? n : defaultVal;
}

/** Extrae el código de idioma base de un locale (ej: "es-AR" → "es", "de-DE" → "de") */
function baseLang(language: string): string {
  return language.split("-")[0].toLowerCase();
}

/** Nombres largos del día (índice 0 = domingo). Para `offerEndsIn` con placeholder `{day}`. */
const WEEKDAY_LONG: Record<string, readonly string[]> = {
  es: ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"],
  en: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
  pt: ["domingo", "segunda-feira", "terça-feira", "quarta-feira", "quinta-feira", "sexta-feira", "sábado"],
  fr: ["dimanche", "lundi", "mardi", "mercredi", "jeudi", "vendredi", "samedi"],
  de: ["Sonntag", "Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag"],
  it: ["domenica", "lunedì", "martedì", "mercoledì", "giovedì", "venerdì", "sabato"],
};

function capitalizeWeekday(label: string): string {
  if (!label) return label;
  return label.charAt(0).toLocaleUpperCase() + label.slice(1);
}

/**
 * Sustituye `{day}` en el template por el nombre del día actual en el idioma indicado.
 */
export function resolveOfferEndsInLabel(
  template: string,
  language: string,
  now: Date = new Date(),
): string {
  const lang = baseLang(language);
  const list = WEEKDAY_LONG[lang] ?? WEEKDAY_LONG.en;
  const raw = list[now.getDay()] ?? list[0];
  const day = capitalizeWeekday(raw);
  return template.includes("{day}") ? template.replace("{day}", day) : template;
}

// ─── Precios ──────────────────────────────────────────────────────────────────

/** Paridad USD→EUR para precios EUR por defecto cuando no hay env específico (ej. 0.92). */
function readUsdEurParity(fallback = 0.92): number {
  const raw = String(import.meta.env.VITE_PREMIUM_USD_EUR_PARITY ?? "").trim();
  const n = raw ? parseFloat(raw) : NaN;
  return Number.isFinite(n) && n > 0.05 && n < 4 ? n : fallback;
}

export const PREMIUM_USD_EUR_PARITY = readUsdEurParity();

/** Centavos EUR default para plan Basic Telegram cuando no hay `VITE_TELEGRAM_BASIC_EUR_AMOUNT_CENTS`. */
export function defaultTelegramBasicEurCentsFromUsd(usdCents: number): number {
  return Math.round(usdCents * PREMIUM_USD_EUR_PARITY);
}

const PRICES_PARTIAL = {
  ars: {
    standard: parsePrice(
      import.meta.env.VITE_PREMIUM_ARS_STANDARD_PRICE as string | undefined,
      29999,
    ),
    vip: parsePrice(
      import.meta.env.VITE_PREMIUM_ARS_VIP_PRICE as string | undefined,
      49999,
    ),
  },
  usd: {
    // Defaults = CANONICAL_MEMBERSHIP_PRODUCTS (Standard $9.99 / Exclusive $29.99)
    standard: parsePrice(
      import.meta.env.VITE_PREMIUM_USD_STANDARD_PRICE as string | undefined,
      CANONICAL_STANDARD_PRICE_USD,
    ),
    vip: parsePrice(
      import.meta.env.VITE_PREMIUM_USD_VIP_PRICE as string | undefined,
      CANONICAL_VIP_PRICE_USD,
    ),
  },
};

const PRICES = {
  ...PRICES_PARTIAL,
  eur: {
    standard: parsePrice(
      import.meta.env.VITE_PREMIUM_EUR_STANDARD_PRICE as string | undefined,
      Math.round(PRICES_PARTIAL.usd.standard * PREMIUM_USD_EUR_PARITY * 100) / 100,
    ),
    vip: parsePrice(
      import.meta.env.VITE_PREMIUM_EUR_VIP_PRICE as string | undefined,
      Math.round(PRICES_PARTIAL.usd.vip * PREMIUM_USD_EUR_PARITY * 100) / 100,
    ),
  },
};

// ─── Textos localizados ───────────────────────────────────────────────────────

/**
 * Textos completos de la UI del template según idioma base.
 * Incluye labels de planes, UI states, checkout, errores y footer.
 * Fallback a inglés para cualquier idioma no mapeado.
 */
export interface BioTemplatePremiumLabels {
  // Planes
  standardName: string;
  standardSubtitle: string;
  vipName: string;
  vipSubtitle: string;
  channelLabel: string;
  /** Card gratuita (Telegram u otro enlace) */
  freePlanTitle: string;
  freePlanSubtitle: string;
  freePlanCta: string;
  // Perfil
  profileLabel: string;
  // CTAs
  ctaDefault: string;
  joinVip: string;
  watchVideo: string;
  chooseAccess: string;
  // Checkout panel
  checkoutTitle: string;
  checkoutProduct: string;
  checkoutPrice: string;
  checkoutPerMonth: string;
  checkoutPerThreeMonths: string;
  checkoutDescSubscription: string;
  checkoutDescQuarterly: string;
  checkoutDescOneTime: string;
  checkoutPay: string;
  checkoutPreparing: string;
  checkoutRedirecting: string;
  checkoutBack: string;
  checkoutClose: string;
  checkoutStep1: string;
  checkoutStep1Desc: string;
  months: string;
  // Checkout payment header
  securePaymentMP: string;
  specialOfferPayment: string;
  securePaymentStripe: string;
  // Footer
  footerSecure: string;
  footerInstant: string;
  footerSubscription: string;
  footerPayment: string;
  footerCode: string;
  // Estados
  loading: string;
  codeLabel: string;
  expiresIn: string;
  discount: string;
  spots: string;
  // Validación de email
  emailRequired: string;
  emailInvalid: string;
  emailRequiredCheckout: string;
  // Errors con recovery email
  connectionError: string;
  errorLinkGenerated: string;
  // Redirect errors (with HTML — use .replace("{url}", checkoutUrl) and dangerouslySetInnerHTML)
  checkoutRedirectPart1: string;
  checkoutRedirectLinkText: string;
  checkoutRedirectPart2: string;
  checkoutPopupPart1: string;
  checkoutPopupPart2: string;
  // Billing interval selector
  subscriptionDuration: string;
  monthly: string;
  quarterly: string;
  bestValue: string;
  threeMonths: string;
  /** Usar con .replace("{pct}", String(Math.round(rate * 100))) */
  quarterlyExtraDiscount: string;
  monthlyAutorenew: string;
  // Email input
  emailPlaceholder: string;
  emailReceiptNote: string;
  continueToCardPayment: string;
  secureEncryptedPayment: string;
  // Special offer / badges
  specialOfferLimited: string;
  /** Incluir `{day}` para el nombre del día actual (p. ej. "🔥 Solo hoy {day}"). */
  offerEndsIn: string;
  dontMissOut: string;
  exclusiveDiscount: string;
  specialOffer: string;
  chooseMembership: string;
  // Footer
  termsAndConditions: string;
  // Best Friend Instagram (Close Friends)
  bestfriendInstagramTitle: string;
  bestfriendInstagramMonthlySubscription: string;
  bestfriendInstagramSubscribe: string;
  bestfriendInstagramProcessing: string;
  bestfriendInstagramSecurePayment: string;
  bestfriendInstagramCancelAnytime: string;
  // Modal de descuento
  discountWait: string;
  /** Usar con .replace("{pct}", String(Math.round(rate * 100))) */
  discountGiftPct: string;
  discountVipSub: string;
  discountOriginalPrice: string;
  discountYourPrice: string;
  /** Usar con .replace("{amount}", formatPrice(savings)) */
  discountYouSave: string;
  discountOneTimeOffer: string;
  discountAccept: string;
  discountDecline: string;
  // Errores
  errorInvalidTitle: string;
  errorInvalidDefault: string;
  errorExpiredTitle: string;
  errorExpiredMessage: string;
  errorUsedTitle: string;
  errorBack: string;
  errorHelp: string;
  // VIP Disabled
  vipCapacityFull: string;
  vipContactForAvailability: string;
  // ── VIP Stripe checkout panel (MembresiaPremiumVip post-Mux flow) ───────────
  // Strings dedicados al panel de checkout que abre después del video VIP de Mux
  // en el flujo internacional (USD / Stripe). Coherencia idiomática con el video.
  vipCheckoutBenefitAccess: string;
  vipCheckoutBenefitCancel: string;
  vipCheckoutBenefitStripe: string;
  vipCheckoutStripeBadge: string;
  vipCheckoutStripeSecurity: string;
  vipCheckoutAcceptedCards: string;
  vipCheckoutProcessing: string;
  vipCheckoutCancelAnytime: string;
  /** Sufijo de precio: "USD/mes" en es, "USD/mo" en en, "USD/mês" en pt, etc. */
  vipCheckoutPriceUnit: string;
  /** Aria-label del botón cerrar (X). */
  vipCheckoutClose: string;
}

const LOCALE_LABELS: Record<string, BioTemplatePremiumLabels> = {
  es: {
    standardName: "Miembro Exclusivo",
    standardSubtitle: "por mes",
    vipName: "Membresía Exclusiva",
    vipSubtitle: "Por Mes - Grupo Privado - Acceso a Plataforma VIP",
    channelLabel: "Membresía Exclusiva",
    freePlanTitle: "Gratis",
    freePlanSubtitle: "Comunidad pública — podés entrar sin pagar.",
    freePlanCta: "Acceso Gratis",
    profileLabel: "Internacional",
    ctaDefault: "Unirte al canal",
    joinVip: "Miembro Exclusivo",
    watchVideo: "Ver video",
    chooseAccess: "Elegí tu acceso",
    checkoutTitle: "Checkout",
    checkoutProduct: "Producto",
    checkoutPrice: "Precio",
    checkoutPerMonth: "/ mes",
    checkoutPerThreeMonths: "/ 3 meses",
    checkoutDescSubscription: "Suscripción mensual. Podés cancelar cuando quieras.",
    checkoutDescQuarterly: "Suscripción trimestral. Se renueva cada 3 meses.",
    checkoutDescOneTime: "Pago único. Acceso inmediato al contenido exclusivo.",
    checkoutPay: "Pagar con",
    checkoutPreparing: "Preparando pago...",
    checkoutRedirecting: "Redirigiendo a",
    checkoutBack: "Volver",
    checkoutClose: "Cerrar",
    checkoutStep1: "Paso 1 de 2",
    checkoutStep1Desc: "Confirmás tu email y luego cargás tu tarjeta aquí. Pago seguro con Mercado Pago.",
    months: "meses",
    securePaymentMP: "Pago seguro con Mercado Pago",
    specialOfferPayment: "🔥 Oferta Especial · Pago seguro",
    securePaymentStripe: "Pago seguro con Stripe",
    footerSecure: "Pago seguro",
    footerInstant: "Acceso inmediato",
    footerSubscription: "Suscripción mensual",
    footerPayment: "Pago seguro",
    footerCode: "Código:",
    loading: "Verificando invitación...",
    codeLabel: "Código",
    expiresIn: "Expira en:",
    discount: "Descuento",
    spots: "Lugares",
    emailRequired: "El email es obligatorio",
    emailInvalid: "Ingresa un email válido",
    emailRequiredCheckout: "Completá tu email para continuar con el pago.",
    connectionError: "Error de conexión. 📧 Te enviamos un email para que puedas intentarlo nuevamente.",
    errorLinkGenerated: "Error al generar enlace de pago. 📧 Te enviamos el link por email.",
    checkoutRedirectPart1: "El checkout está listo pero no se pudo abrir automáticamente.",
    checkoutRedirectLinkText: "Haz clic aquí para continuar",
    checkoutRedirectPart2: "📧 También te enviamos el link por email.",
    checkoutPopupPart1: "No se pudo abrir el checkout.",
    checkoutPopupPart2: "o desactiva tu bloqueador de anuncios.",
    subscriptionDuration: "Duración de suscripción",
    monthly: "Mensual",
    quarterly: "Trimestral",
    bestValue: "🔥 MEJOR VALOR",
    threeMonths: "3 meses",
    quarterlyExtraDiscount: "✨ {pct}% extra de descuento aplicado. Se renueva automáticamente cada 3 meses.",
    monthlyAutorenew: "Suscripción mensual. Se renueva automáticamente cada mes.",
    emailPlaceholder: "tu@email.com",
    emailReceiptNote: "Necesario para tu recibo y confirmación",
    continueToCardPayment: "Continuar al pago con tarjeta",
    secureEncryptedPayment: "Pago 100% seguro y encriptado",
    specialOfferLimited: "Oferta Especial Limitada",
    offerEndsIn: "🔥 Solo hoy {day}",
    dontMissOut: "¡No te lo pierdas!",
    exclusiveDiscount: "Descuento Exclusivo",
    specialOffer: "Oferta Especial",
    chooseMembership: "Elegí tu membresía",
    termsAndConditions: "Términos y Condiciones",
    bestfriendInstagramTitle: "Sumate a mis Close Friends",
    bestfriendInstagramMonthlySubscription: "Suscripción mensual",
    bestfriendInstagramSubscribe: "Suscribirme",
    bestfriendInstagramProcessing: "Procesando...",
    bestfriendInstagramSecurePayment: "Pago seguro con",
    bestfriendInstagramCancelAnytime: "Cancelá cuando quieras",
    discountWait: "¡Espera!",
    discountGiftPct: "Te regalo un {pct}% de descuento",
    discountVipSub: "en tu suscripción VIP",
    discountOriginalPrice: "Precio original",
    discountYourPrice: "Tu precio",
    discountYouSave: "Ahorrás {amount}",
    discountOneTimeOffer: "Oferta única",
    discountAccept: "Aceptar descuento",
    discountDecline: "No, gracias",
    errorInvalidTitle: "Invitación no válida",
    errorInvalidDefault:
      "El código de invitación no existe o es incorrecto. Verifica que tengas el enlace correcto.",
    errorExpiredTitle: "Invitación expirada",
    errorExpiredMessage:
      "Esta invitación ha expirado y ya no está disponible. Contacta para obtener una nueva.",
    errorUsedTitle: "Límite alcanzado",
    errorBack: "Volver al inicio",
    errorHelp: "¿Necesitas ayuda? Contacta al administrador",
    vipCapacityFull: "Completo",
    vipContactForAvailability: "No disponible",
    vipCheckoutBenefitAccess: "Acceso al contenido VIP completo",
    vipCheckoutBenefitCancel: "Cancelable cuando quieras",
    vipCheckoutBenefitStripe: "Pago procesado por Stripe — nunca vemos tu tarjeta",
    vipCheckoutStripeBadge: "Pago procesado por",
    vipCheckoutStripeSecurity: "Stripe procesa tu pago bajo PCI DSS Level 1, el estándar más alto de seguridad de la industria. Tus datos de tarjeta nunca pasan por nuestros servidores.",
    vipCheckoutAcceptedCards: "Aceptamos",
    vipCheckoutProcessing: "Procesando...",
    vipCheckoutCancelAnytime: "Cancelable en cualquier momento",
    vipCheckoutPriceUnit: "USD/mes",
    vipCheckoutClose: "Cerrar",
  },
  en: {
    standardName: "Exclusive Member",
    standardSubtitle: "per month",
    vipName: "Exclusive Member",
    vipSubtitle: "Per month - Private group - VIP platform access",
    channelLabel: "Exclusive Member",
    freePlanTitle: "Free",
    freePlanSubtitle: "Public community — join with no payment.",
    freePlanCta: "Free Access",
    profileLabel: "International",
    ctaDefault: "Join the channel",
    joinVip: "Exclusive Member",
    watchVideo: "Watch video",
    chooseAccess: "Choose your access",
    checkoutTitle: "Checkout",
    checkoutProduct: "Product",
    checkoutPrice: "Price",
    checkoutPerMonth: "/ mo",
    checkoutPerThreeMonths: "/ 3 mo",
    checkoutDescSubscription: "Monthly subscription. Cancel anytime.",
    checkoutDescQuarterly: "Quarterly subscription. Renews every 3 months.",
    checkoutDescOneTime: "One-time payment. Immediate access to exclusive content.",
    checkoutPay: "Pay with",
    checkoutPreparing: "Preparing payment...",
    checkoutRedirecting: "Redirecting to",
    checkoutBack: "Go back",
    checkoutClose: "Close",
    checkoutStep1: "Step 1 of 2",
    checkoutStep1Desc: "Confirm your email, then enter your card here. Secure payment with Mercado Pago.",
    months: "months",
    securePaymentMP: "Secure payment with Mercado Pago",
    specialOfferPayment: "🔥 Special Offer · Secure payment",
    securePaymentStripe: "Secure payment with Stripe",
    footerSecure: "Secure payment",
    footerInstant: "Instant access",
    footerSubscription: "Monthly subscription",
    footerPayment: "Secure payment",
    footerCode: "Code:",
    loading: "Verifying invitation...",
    codeLabel: "Code",
    expiresIn: "Expires in:",
    discount: "Discount",
    spots: "Spots",
    emailRequired: "Email is required",
    emailInvalid: "Enter a valid email",
    emailRequiredCheckout: "Enter your email to continue to checkout.",
    connectionError: "Connection error. 📧 We sent you an email so you can try again.",
    errorLinkGenerated: "Failed to generate payment link. 📧 We sent you the link via email.",
    checkoutRedirectPart1: "Your checkout is ready but couldn't open automatically.",
    checkoutRedirectLinkText: "Click here to continue",
    checkoutRedirectPart2: "📧 We also sent you the link via email.",
    checkoutPopupPart1: "Couldn't open checkout.",
    checkoutPopupPart2: "or disable your ad blocker.",
    subscriptionDuration: "Subscription duration",
    monthly: "Monthly",
    quarterly: "Quarterly",
    bestValue: "🔥 BEST VALUE",
    threeMonths: "3 months",
    quarterlyExtraDiscount: "✨ {pct}% extra discount applied. Auto-renews every 3 months.",
    monthlyAutorenew: "Monthly subscription. Auto-renews every month.",
    emailPlaceholder: "your@email.com",
    emailReceiptNote: "Required for your receipt and confirmation",
    continueToCardPayment: "Continue to card payment",
    secureEncryptedPayment: "100% secure encrypted payment",
    specialOfferLimited: "Limited Special Offer",
    offerEndsIn: "🔥 Only today · {day}",
    dontMissOut: "Don't miss out!",
    exclusiveDiscount: "Exclusive Discount",
    specialOffer: "Special Offer",
    chooseMembership: "Choose your membership",
    termsAndConditions: "Terms and Conditions",
    bestfriendInstagramTitle: "Join my Close Friends",
    bestfriendInstagramMonthlySubscription: "Monthly subscription",
    bestfriendInstagramSubscribe: "Subscribe",
    bestfriendInstagramProcessing: "Processing...",
    bestfriendInstagramSecurePayment: "Secure payment with",
    bestfriendInstagramCancelAnytime: "Cancel anytime",
    discountWait: "Wait!",
    discountGiftPct: "I gift you a {pct}% discount",
    discountVipSub: "on your VIP subscription",
    discountOriginalPrice: "Original price",
    discountYourPrice: "Your price",
    discountYouSave: "You save {amount}",
    discountOneTimeOffer: "One-time offer",
    discountAccept: "Accept discount",
    discountDecline: "No, thanks",
    errorInvalidTitle: "Invalid invitation",
    errorInvalidDefault:
      "The invitation code does not exist or is incorrect. Please check that you have the correct link.",
    errorExpiredTitle: "Invitation expired",
    errorExpiredMessage:
      "This invitation has expired and is no longer available. Contact us to get a new one.",
    errorUsedTitle: "Limit reached",
    errorBack: "Back to home",
    errorHelp: "Need help? Contact the administrator",
    vipCapacityFull: "Full",
    vipContactForAvailability: "Not available",
    vipCheckoutBenefitAccess: "Full VIP content access",
    vipCheckoutBenefitCancel: "Cancel anytime",
    vipCheckoutBenefitStripe: "Payment processed by Stripe — we never see your card",
    vipCheckoutStripeBadge: "Payments powered by",
    vipCheckoutStripeSecurity: "Stripe processes your payment under PCI DSS Level 1, the highest security standard in the industry. Your card details never touch our servers.",
    vipCheckoutAcceptedCards: "We accept",
    vipCheckoutProcessing: "Processing...",
    vipCheckoutCancelAnytime: "Cancel anytime",
    vipCheckoutPriceUnit: "USD/mo",
    vipCheckoutClose: "Close",
  },
  pt: {
    standardName: "Membro Exclusivo",
    standardSubtitle: "por mês",
    vipName: "Membership Exclusiva",
    vipSubtitle: "Por mês - Grupo privado - Acesso à plataforma VIP",
    channelLabel: "Assinatura Standard",
    freePlanTitle: "Grátis",
    freePlanSubtitle: "Comunidade pública — entre sem pagar.",
    freePlanCta: "Acesso Grátis",
    profileLabel: "Internacional",
    ctaDefault: "Entrar no canal",
    joinVip: "Membro Exclusivo",
    watchVideo: "Ver vídeo",
    chooseAccess: "Escolha seu acesso",
    checkoutTitle: "Checkout",
    checkoutProduct: "Produto",
    checkoutPrice: "Preço",
    checkoutPerMonth: "/ mês",
    checkoutPerThreeMonths: "/ 3 meses",
    checkoutDescSubscription: "Assinatura mensal. Cancele quando quiser.",
    checkoutDescQuarterly: "Assinatura trimestral. Renova a cada 3 meses.",
    checkoutDescOneTime: "Pagamento único. Acesso imediato ao conteúdo exclusivo.",
    checkoutPay: "Pagar com",
    checkoutPreparing: "Preparando pagamento...",
    checkoutRedirecting: "Redirecionando para",
    checkoutBack: "Voltar",
    checkoutClose: "Fechar",
    checkoutStep1: "Passo 1 de 2",
    checkoutStep1Desc: "Confirme seu email e depois insira seu cartão aqui. Pagamento seguro com Mercado Pago.",
    months: "meses",
    securePaymentMP: "Pagamento seguro com Mercado Pago",
    specialOfferPayment: "🔥 Oferta Especial · Pagamento seguro",
    securePaymentStripe: "Pagamento seguro com Stripe",
    footerSecure: "Pagamento seguro",
    footerInstant: "Acesso imediato",
    footerSubscription: "Assinatura mensal",
    footerPayment: "Pagamento seguro",
    footerCode: "Código:",
    loading: "Verificando convite...",
    codeLabel: "Código",
    expiresIn: "Expira em:",
    discount: "Desconto",
    spots: "Vagas",
    emailRequired: "O email é obrigatório",
    emailInvalid: "Insira um email válido",
    emailRequiredCheckout: "Preencha seu email para continuar com o pagamento.",
    connectionError: "Erro de conexão. 📧 Enviamos um email para você tentar novamente.",
    errorLinkGenerated: "Erro ao gerar link de pagamento. 📧 Enviamos o link por email.",
    checkoutRedirectPart1: "O checkout está pronto mas não foi possível abrir automaticamente.",
    checkoutRedirectLinkText: "Clique aqui para continuar",
    checkoutRedirectPart2: "📧 Também enviamos o link por email.",
    checkoutPopupPart1: "Não foi possível abrir o checkout.",
    checkoutPopupPart2: "ou desative seu bloqueador de anúncios.",
    subscriptionDuration: "Duração da assinatura",
    monthly: "Mensal",
    quarterly: "Trimestral",
    bestValue: "🔥 MELHOR VALOR",
    threeMonths: "3 meses",
    quarterlyExtraDiscount: "✨ {pct}% de desconto extra aplicado. Renova automaticamente a cada 3 meses.",
    monthlyAutorenew: "Assinatura mensal. Renova automaticamente todo mês.",
    emailPlaceholder: "seu@email.com",
    emailReceiptNote: "Necessário para seu recibo e confirmação",
    continueToCardPayment: "Continuar para pagamento com cartão",
    secureEncryptedPayment: "Pagamento 100% seguro e criptografado",
    specialOfferLimited: "Oferta Especial Limitada",
    offerEndsIn: "🔥 Só hoje · {day}",
    dontMissOut: "Não perca!",
    exclusiveDiscount: "Desconto Exclusivo",
    specialOffer: "Oferta Especial",
    chooseMembership: "Escolha sua assinatura",
    termsAndConditions: "Termos e Condições",
    bestfriendInstagramTitle: "Entre nos meus Close Friends",
    bestfriendInstagramMonthlySubscription: "Assinatura mensal",
    bestfriendInstagramSubscribe: "Assinar",
    bestfriendInstagramProcessing: "Processando...",
    bestfriendInstagramSecurePayment: "Pagamento seguro com",
    bestfriendInstagramCancelAnytime: "Cancele quando quiser",
    discountWait: "Espera!",
    discountGiftPct: "Te dou {pct}% de desconto",
    discountVipSub: "na sua assinatura VIP",
    discountOriginalPrice: "Preço original",
    discountYourPrice: "Seu preço",
    discountYouSave: "Você economiza {amount}",
    discountOneTimeOffer: "Oferta única",
    discountAccept: "Aceitar desconto",
    discountDecline: "Não, obrigado",
    errorInvalidTitle: "Convite inválido",
    errorInvalidDefault:
      "O código de convite não existe ou está incorreto. Verifique se você tem o link correto.",
    errorExpiredTitle: "Convite expirado",
    errorExpiredMessage:
      "Este convite expirou e não está mais disponível. Entre em contato para obter um novo.",
    errorUsedTitle: "Limite atingido",
    errorBack: "Voltar ao início",
    errorHelp: "Precisa de ajuda? Entre em contato com o administrador",
    vipCapacityFull: "Completo",
    vipContactForAvailability: "Não disponível",
    vipCheckoutBenefitAccess: "Acesso completo ao conteúdo VIP",
    vipCheckoutBenefitCancel: "Cancele quando quiser",
    vipCheckoutBenefitStripe: "Pagamento processado pelo Stripe — nunca vemos seu cartão",
    vipCheckoutStripeBadge: "Pagamento processado por",
    vipCheckoutStripeSecurity: "O Stripe processa seu pagamento sob PCI DSS Level 1, o mais alto padrão de segurança do setor. Os dados do seu cartão nunca passam pelos nossos servidores.",
    vipCheckoutAcceptedCards: "Aceitamos",
    vipCheckoutProcessing: "Processando...",
    vipCheckoutCancelAnytime: "Cancele a qualquer momento",
    vipCheckoutPriceUnit: "USD/mês",
    vipCheckoutClose: "Fechar",
  },
  fr: {
    standardName: "Membre Exclusif",
    standardSubtitle: "par mois",
    vipName: "Adhésion Exclusive",
    vipSubtitle: "Par mois - Groupe privé - Accès à la plateforme VIP",
    channelLabel: "Abonnement Standard",
    freePlanTitle: "Gratuit",
    freePlanSubtitle: "Communauté publique — rejoignez sans payer.",
    freePlanCta: "Accès Gratuit",
    profileLabel: "International",
    ctaDefault: "Rejoindre le canal",
    joinVip: "Membre Exclusif",
    watchVideo: "Voir la vidéo",
    chooseAccess: "Choisissez votre accès",
    checkoutTitle: "Paiement",
    checkoutProduct: "Produit",
    checkoutPrice: "Prix",
    checkoutPerMonth: "/ mois",
    checkoutPerThreeMonths: "/ 3 mois",
    checkoutDescSubscription: "Abonnement mensuel. Annulez à tout moment.",
    checkoutDescQuarterly: "Abonnement trimestriel. Se renouvelle tous les 3 mois.",
    checkoutDescOneTime: "Paiement unique. Accès immédiat au contenu exclusif.",
    checkoutPay: "Payer avec",
    checkoutPreparing: "Préparation du paiement...",
    checkoutRedirecting: "Redirection vers",
    checkoutBack: "Retour",
    checkoutClose: "Fermer",
    checkoutStep1: "Étape 1 sur 2",
    checkoutStep1Desc: "Confirmez votre email, puis entrez votre carte ici. Paiement sécurisé avec Mercado Pago.",
    months: "mois",
    securePaymentMP: "Paiement sécurisé avec Mercado Pago",
    specialOfferPayment: "🔥 Offre Spéciale · Paiement sécurisé",
    securePaymentStripe: "Paiement sécurisé avec Stripe",
    footerSecure: "Paiement sécurisé",
    footerInstant: "Accès immédiat",
    footerSubscription: "Abonnement mensuel",
    footerPayment: "Paiement sécurisé",
    footerCode: "Code:",
    loading: "Vérification de l'invitation...",
    codeLabel: "Code",
    expiresIn: "Expire dans:",
    discount: "Réduction",
    spots: "Places",
    emailRequired: "L'email est obligatoire",
    emailInvalid: "Entrez un email valide",
    emailRequiredCheckout: "Complétez votre email pour continuer le paiement.",
    connectionError: "Erreur de connexion. 📧 Nous vous avons envoyé un email pour réessayer.",
    errorLinkGenerated: "Erreur lors de la génération du lien de paiement. 📧 Nous vous avons envoyé le lien par email.",
    checkoutRedirectPart1: "Le paiement est prêt mais n'a pas pu s'ouvrir automatiquement.",
    checkoutRedirectLinkText: "Cliquez ici pour continuer",
    checkoutRedirectPart2: "📧 Nous vous avons également envoyé le lien par email.",
    checkoutPopupPart1: "Impossible d'ouvrir le paiement.",
    checkoutPopupPart2: "ou désactivez votre bloqueur de publicités.",
    subscriptionDuration: "Durée de l'abonnement",
    monthly: "Mensuel",
    quarterly: "Trimestriel",
    bestValue: "🔥 MEILLEURE VALEUR",
    threeMonths: "3 mois",
    quarterlyExtraDiscount: "✨ {pct}% de réduction supplémentaire appliquée. Se renouvelle automatiquement tous les 3 mois.",
    monthlyAutorenew: "Abonnement mensuel. Se renouvelle automatiquement chaque mois.",
    emailPlaceholder: "votre@email.com",
    emailReceiptNote: "Nécessaire pour votre reçu et confirmation",
    continueToCardPayment: "Continuer vers le paiement par carte",
    secureEncryptedPayment: "Paiement 100% sécurisé et crypté",
    specialOfferLimited: "Offre Spéciale Limitée",
    offerEndsIn: "🔥 Aujourd'hui seulement · {day}",
    dontMissOut: "Ne manquez pas ça!",
    exclusiveDiscount: "Réduction Exclusive",
    specialOffer: "Offre Spéciale",
    chooseMembership: "Choisissez votre abonnement",
    termsAndConditions: "Termes et Conditions",
    bestfriendInstagramTitle: "Rejoignez mes Close Friends",
    bestfriendInstagramMonthlySubscription: "Abonnement mensuel",
    bestfriendInstagramSubscribe: "S'abonner",
    bestfriendInstagramProcessing: "Traitement...",
    bestfriendInstagramSecurePayment: "Paiement sécurisé avec",
    bestfriendInstagramCancelAnytime: "Annuler à tout moment",
    discountWait: "Attendez!",
    discountGiftPct: "Je vous offre {pct}% de réduction",
    discountVipSub: "sur votre abonnement VIP",
    discountOriginalPrice: "Prix original",
    discountYourPrice: "Votre prix",
    discountYouSave: "Vous économisez {amount}",
    discountOneTimeOffer: "Offre unique",
    discountAccept: "Accepter la réduction",
    discountDecline: "Non, merci",
    errorInvalidTitle: "Invitation invalide",
    errorInvalidDefault:
      "Le code d'invitation n'existe pas ou est incorrect. Vérifiez que vous avez le bon lien.",
    errorExpiredTitle: "Invitation expirée",
    errorExpiredMessage:
      "Cette invitation a expiré et n'est plus disponible. Contactez-nous pour en obtenir une nouvelle.",
    errorUsedTitle: "Limite atteinte",
    errorBack: "Retour à l'accueil",
    errorHelp: "Besoin d'aide? Contactez l'administrateur",
    vipCapacityFull: "Complet",
    vipContactForAvailability: "Non disponible",
    vipCheckoutBenefitAccess: "Accès complet au contenu VIP",
    vipCheckoutBenefitCancel: "Annulable à tout moment",
    vipCheckoutBenefitStripe: "Paiement traité par Stripe — nous ne voyons jamais votre carte",
    vipCheckoutStripeBadge: "Paiement sécurisé par",
    vipCheckoutStripeSecurity: "Stripe traite votre paiement sous PCI DSS Level 1, le plus haut standard de sécurité du secteur. Les données de votre carte ne transitent jamais par nos serveurs.",
    vipCheckoutAcceptedCards: "Nous acceptons",
    vipCheckoutProcessing: "Traitement...",
    vipCheckoutCancelAnytime: "Annulable à tout moment",
    vipCheckoutPriceUnit: "USD/mois",
    vipCheckoutClose: "Fermer",
  },
  de: {
    standardName: "Exklusives Mitglied",
    standardSubtitle: "pro Monat",
    vipName: "Exklusive Mitgliedschaft",
    vipSubtitle: "Pro Monat - Private Gruppe - Zugang zur VIP-Plattform",
    channelLabel: "Standard-Mitgliedschaft",
    freePlanTitle: "Kostenlos",
    freePlanSubtitle: "Öffentliche Community — kostenlos beitreten.",
    freePlanCta: "Kostenloser Zugang",
    profileLabel: "International",
    ctaDefault: "Dem Kanal beitreten",
    joinVip: "Exklusives Mitglied",
    watchVideo: "Video ansehen",
    chooseAccess: "Wählen Sie Ihren Zugang",
    checkoutTitle: "Kasse",
    checkoutProduct: "Produkt",
    checkoutPrice: "Preis",
    checkoutPerMonth: "/ Monat",
    checkoutPerThreeMonths: "/ 3 Monate",
    checkoutDescSubscription: "Monatliches Abonnement. Jederzeit kündbar.",
    checkoutDescQuarterly: "Vierteljährliches Abonnement. Verlängert sich alle 3 Monate.",
    checkoutDescOneTime: "Einmalige Zahlung. Sofortiger Zugang zu exklusiven Inhalten.",
    checkoutPay: "Bezahlen mit",
    checkoutPreparing: "Zahlung wird vorbereitet...",
    checkoutRedirecting: "Weiterleitung zu",
    checkoutBack: "Zurück",
    checkoutClose: "Schließen",
    checkoutStep1: "Schritt 1 von 2",
    checkoutStep1Desc: "Bestätigen Sie Ihre E-Mail und geben Sie dann hier Ihre Karte ein. Sichere Zahlung mit Mercado Pago.",
    months: "Monate",
    securePaymentMP: "Sichere Zahlung mit Mercado Pago",
    specialOfferPayment: "🔥 Sonderangebot · Sichere Zahlung",
    securePaymentStripe: "Sichere Zahlung mit Stripe",
    footerSecure: "Sichere Zahlung",
    footerInstant: "Sofortiger Zugang",
    footerSubscription: "Monatliches Abonnement",
    footerPayment: "Sichere Zahlung",
    footerCode: "Code:",
    loading: "Einladung wird überprüft...",
    codeLabel: "Code",
    expiresIn: "Läuft ab in:",
    discount: "Rabatt",
    spots: "Plätze",
    emailRequired: "E-Mail ist erforderlich",
    emailInvalid: "Geben Sie eine gültige E-Mail ein",
    emailRequiredCheckout: "Geben Sie Ihre E-Mail ein, um mit der Zahlung fortzufahren.",
    connectionError: "Verbindungsfehler. 📧 Wir haben Ihnen eine E-Mail gesendet, damit Sie es erneut versuchen können.",
    errorLinkGenerated: "Fehler beim Generieren des Zahlungslinks. 📧 Wir haben Ihnen den Link per E-Mail gesendet.",
    checkoutRedirectPart1: "Ihre Kasse ist bereit, konnte aber nicht automatisch geöffnet werden.",
    checkoutRedirectLinkText: "Klicken Sie hier, um fortzufahren",
    checkoutRedirectPart2: "📧 Wir haben Ihnen den Link auch per E-Mail gesendet.",
    checkoutPopupPart1: "Kasse konnte nicht geöffnet werden.",
    checkoutPopupPart2: "oder deaktivieren Sie Ihren Werbeblocker.",
    subscriptionDuration: "Abonnementdauer",
    monthly: "Monatlich",
    quarterly: "Vierteljährlich",
    bestValue: "🔥 BESTER WERT",
    threeMonths: "3 Monate",
    quarterlyExtraDiscount: "✨ {pct}% zusätzlicher Rabatt angewendet. Verlängert sich automatisch alle 3 Monate.",
    monthlyAutorenew: "Monatliches Abonnement. Verlängert sich automatisch jeden Monat.",
    emailPlaceholder: "ihre@email.com",
    emailReceiptNote: "Erforderlich für Ihre Quittung und Bestätigung",
    continueToCardPayment: "Weiter zur Kartenzahlung",
    secureEncryptedPayment: "100% sichere verschlüsselte Zahlung",
    specialOfferLimited: "Begrenztes Sonderangebot",
    offerEndsIn: "🔥 Nur heute · {day}",
    dontMissOut: "Verpassen Sie es nicht!",
    exclusiveDiscount: "Exklusiver Rabatt",
    specialOffer: "Sonderangebot",
    chooseMembership: "Wählen Sie Ihre Mitgliedschaft",
    termsAndConditions: "Allgemeine Geschäftsbedingungen",
    bestfriendInstagramTitle: "Werde mein Close Friend",
    bestfriendInstagramMonthlySubscription: "Monatliches Abonnement",
    bestfriendInstagramSubscribe: "Abonnieren",
    bestfriendInstagramProcessing: "Verarbeitung...",
    bestfriendInstagramSecurePayment: "Sichere Zahlung mit",
    bestfriendInstagramCancelAnytime: "Jederzeit kündbar",
    discountWait: "Warten Sie!",
    discountGiftPct: "Ich schenke Ihnen {pct}% Rabatt",
    discountVipSub: "auf Ihr VIP-Abonnement",
    discountOriginalPrice: "Originalpreis",
    discountYourPrice: "Ihr Preis",
    discountYouSave: "Sie sparen {amount}",
    discountOneTimeOffer: "Einmaliges Angebot",
    discountAccept: "Rabatt akzeptieren",
    discountDecline: "Nein, danke",
    errorInvalidTitle: "Ungültige Einladung",
    errorInvalidDefault:
      "Der Einladungscode existiert nicht oder ist falsch. Bitte überprüfen Sie, ob Sie den richtigen Link haben.",
    errorExpiredTitle: "Einladung abgelaufen",
    errorExpiredMessage:
      "Diese Einladung ist abgelaufen und nicht mehr verfügbar. Kontaktieren Sie uns, um eine neue zu erhalten.",
    errorUsedTitle: "Limit erreicht",
    errorBack: "Zurück zur Startseite",
    errorHelp: "Brauchen Sie Hilfe? Kontaktieren Sie den Administrator",
    vipCapacityFull: "Voll",
    vipContactForAvailability: "Nicht verfügbar",
    vipCheckoutBenefitAccess: "Vollständiger VIP-Zugang",
    vipCheckoutBenefitCancel: "Jederzeit kündbar",
    vipCheckoutBenefitStripe: "Zahlung über Stripe — wir sehen deine Karte nie",
    vipCheckoutStripeBadge: "Zahlung abgewickelt von",
    vipCheckoutStripeSecurity: "Stripe verarbeitet deine Zahlung nach PCI DSS Level 1, dem höchsten Sicherheitsstandard der Branche. Deine Kartendaten erreichen niemals unsere Server.",
    vipCheckoutAcceptedCards: "Wir akzeptieren",
    vipCheckoutProcessing: "Wird verarbeitet...",
    vipCheckoutCancelAnytime: "Jederzeit kündbar",
    vipCheckoutPriceUnit: "USD/Monat",
    vipCheckoutClose: "Schließen",
  },
  it: {
    standardName: "Membro Esclusivo",
    standardSubtitle: "al mese",
    vipName: "Iscrizione Esclusiva",
    vipSubtitle: "Al mese - Gruppo privato - Accesso alla piattaforma VIP",
    channelLabel: "Abbonamento Standard",
    freePlanTitle: "Gratis",
    freePlanSubtitle: "Community pubblica — entra senza pagare.",
    freePlanCta: "Accesso Gratuito",
    profileLabel: "Internazionale",
    ctaDefault: "Unisciti al canale",
    joinVip: "Membro Esclusivo",
    watchVideo: "Guarda il video",
    chooseAccess: "Scegli il tuo accesso",
    checkoutTitle: "Checkout",
    checkoutProduct: "Prodotto",
    checkoutPrice: "Prezzo",
    checkoutPerMonth: "/ mese",
    checkoutPerThreeMonths: "/ 3 mesi",
    checkoutDescSubscription: "Abbonamento mensile. Annulla quando vuoi.",
    checkoutDescQuarterly: "Abbonamento trimestrale. Si rinnova ogni 3 mesi.",
    checkoutDescOneTime: "Pagamento unico. Accesso immediato ai contenuti esclusivi.",
    checkoutPay: "Paga con",
    checkoutPreparing: "Preparazione del pagamento...",
    checkoutRedirecting: "Reindirizzamento a",
    checkoutBack: "Indietro",
    checkoutClose: "Chiudi",
    checkoutStep1: "Passo 1 di 2",
    checkoutStep1Desc: "Conferma la tua email e poi inserisci la tua carta qui. Pagamento sicuro con Mercado Pago.",
    months: "mesi",
    securePaymentMP: "Pagamento sicuro con Mercado Pago",
    specialOfferPayment: "🔥 Offerta Speciale · Pagamento sicuro",
    securePaymentStripe: "Pagamento sicuro con Stripe",
    footerSecure: "Pagamento sicuro",
    footerInstant: "Accesso immediato",
    footerSubscription: "Abbonamento mensile",
    footerPayment: "Pagamento sicuro",
    footerCode: "Codice:",
    loading: "Verifica dell'invito...",
    codeLabel: "Codice",
    expiresIn: "Scade tra:",
    discount: "Sconto",
    spots: "Posti",
    emailRequired: "L'email è obbligatoria",
    emailInvalid: "Inserisci un'email valida",
    emailRequiredCheckout: "Completa la tua email per continuare con il pagamento.",
    connectionError: "Errore di connessione. 📧 Ti abbiamo inviato un'email per riprovare.",
    errorLinkGenerated: "Errore nella generazione del link di pagamento. 📧 Ti abbiamo inviato il link via email.",
    checkoutRedirectPart1: "Il checkout è pronto ma non si è potuto aprire automaticamente.",
    checkoutRedirectLinkText: "Clicca qui per continuare",
    checkoutRedirectPart2: "📧 Ti abbiamo anche inviato il link via email.",
    checkoutPopupPart1: "Impossibile aprire il checkout.",
    checkoutPopupPart2: "o disattiva il tuo blocco pubblicità.",
    subscriptionDuration: "Durata dell'abbonamento",
    monthly: "Mensile",
    quarterly: "Trimestrale",
    bestValue: "🔥 MIGLIOR VALORE",
    threeMonths: "3 mesi",
    quarterlyExtraDiscount: "✨ {pct}% di sconto extra applicato. Si rinnova automaticamente ogni 3 mesi.",
    monthlyAutorenew: "Abbonamento mensile. Si rinnova automaticamente ogni mese.",
    emailPlaceholder: "tua@email.com",
    emailReceiptNote: "Necessario per la tua ricevuta e conferma",
    continueToCardPayment: "Continua al pagamento con carta",
    secureEncryptedPayment: "Pagamento 100% sicuro e crittografato",
    specialOfferLimited: "Offerta Speciale Limitata",
    offerEndsIn: "🔥 Solo oggi · {day}",
    dontMissOut: "Non perdertelo!",
    exclusiveDiscount: "Sconto Esclusivo",
    specialOffer: "Offerta Speciale",
    chooseMembership: "Scegli il tuo abbonamento",
    termsAndConditions: "Termini e Condizioni",
    bestfriendInstagramTitle: "Unisciti ai miei Close Friends",
    bestfriendInstagramMonthlySubscription: "Abbonamento mensile",
    bestfriendInstagramSubscribe: "Iscriviti",
    bestfriendInstagramProcessing: "Elaborazione...",
    bestfriendInstagramSecurePayment: "Pagamento sicuro con",
    bestfriendInstagramCancelAnytime: "Annulla in qualsiasi momento",
    discountWait: "Aspetta!",
    discountGiftPct: "Ti regalo uno sconto del {pct}%",
    discountVipSub: "sul tuo abbonamento VIP",
    discountOriginalPrice: "Prezzo originale",
    discountYourPrice: "Il tuo prezzo",
    discountYouSave: "Risparmi {amount}",
    discountOneTimeOffer: "Offerta unica",
    discountAccept: "Accetta lo sconto",
    discountDecline: "No, grazie",
    errorInvalidTitle: "Invito non valido",
    errorInvalidDefault:
      "Il codice di invito non esiste o non è corretto. Verifica di avere il link corretto.",
    errorExpiredTitle: "Invito scaduto",
    errorExpiredMessage:
      "Questo invito è scaduto e non è più disponibile. Contattaci per ottenerne uno nuovo.",
    errorUsedTitle: "Limite raggiunto",
    errorBack: "Torna alla home",
    errorHelp: "Hai bisogno di aiuto? Contatta l'amministratore",
    vipCapacityFull: "Completo",
    vipContactForAvailability: "Non disponibile",
    vipCheckoutBenefitAccess: "Accesso completo ai contenuti VIP",
    vipCheckoutBenefitCancel: "Cancellabile in qualsiasi momento",
    vipCheckoutBenefitStripe: "Pagamento elaborato da Stripe — non vediamo mai la tua carta",
    vipCheckoutStripeBadge: "Pagamento gestito da",
    vipCheckoutStripeSecurity: "Stripe elabora il tuo pagamento secondo PCI DSS Level 1, il più alto standard di sicurezza del settore. I dati della tua carta non passano mai dai nostri server.",
    vipCheckoutAcceptedCards: "Accettiamo",
    vipCheckoutProcessing: "Elaborazione...",
    vipCheckoutCancelAnytime: "Cancellabile in qualsiasi momento",
    vipCheckoutPriceUnit: "USD/mese",
    vipCheckoutClose: "Chiudi",
  },
};

/**
 * Devuelve los textos completos de UI para un idioma (fallback a inglés).
 * Se puede usar tanto dentro del config como importar desde el template.
 */
export function getLabelsForLanguage(
  language: string,
): BioTemplatePremiumLabels {
  const lang = baseLang(language);
  return LOCALE_LABELS[lang] ?? LOCALE_LABELS["en"];
}

// alias interno
const getLocaleLabels = getLabelsForLanguage;

// ─── Etiquetas de país ────────────────────────────────────────────────────────

/**
 * Mapa explícito de etiqueta de perfil por país.
 * Cubre exactamente los países que el Worker detecta (COUNTRY_TEMPLATE_MAP).
 * Formato: "<nombre en idioma local>" — sin emojis de bandera para máxima
 * compatibilidad cross-platform (los flag emojis no funcionan en Windows).
 */
const COUNTRY_PROFILE_LABELS: Record<string, string> = {
  // LATAM español
  AR: "Argentina",
  MX: "México",
  CO: "Colombia",
  CL: "Chile",
  PE: "Perú",
  UY: "Uruguay",
  PY: "Paraguay",
  EC: "Ecuador",
  BO: "Bolivia",
  VE: "Venezuela",
  CR: "Costa Rica",
  PA: "Panamá",
  GT: "Guatemala",
  HN: "Honduras",
  SV: "El Salvador",
  NI: "Nicaragua",
  DO: "República Dominicana",
  CU: "Cuba",
  PR: "Puerto Rico",
  // Brasil
  BR: "Brasil",
  // Anglosajón
  US: "United States",
  GB: "United Kingdom",
  IE: "Ireland",
  AU: "Australia",
  CA: "Canada",
  NZ: "New Zealand",
  // Europa continental
  ES: "España",
  DE: "Deutschland",
  FR: "France",
  IT: "Italia",
  PT: "Portugal",
  NL: "Nederland",
  BE: "België",
  CH: "Schweiz",
  AT: "Österreich",
  SE: "Sverige",
  NO: "Norge",
  DK: "Danmark",
  FI: "Suomi",
  PL: "Polska",
  CZ: "Česká republika",
  HU: "Magyarország",
  RO: "România",
  BG: "България",
  HR: "Hrvatska",
  SI: "Slovenija",
  SK: "Slovensko",
  LT: "Lietuva",
  LV: "Latvija",
  EE: "Eesti",
  LU: "Luxembourg",
  MT: "Malta",
  CY: "Κύπρος",
  IS: "Ísland",
  GR: "Ελλάδα",
  // Medio Oriente
  AE: "UAE",
};

// ─── API pública ──────────────────────────────────────────────────────────────

/**
 * Países que pagan en ARS via Mercado Pago.
 * Solo Argentina — el resto paga USD via Stripe.
 */
const ARS_COUNTRIES = new Set(["AR"]);

/** Dado el país detectado por el Worker, retorna la moneda correspondiente. */
export function getCurrencyForCountry(country: string): "ARS" | "USD" {
  return ARS_COUNTRIES.has(country.toUpperCase()) ? "ARS" : "USD";
}

/**
 * Retorna la etiqueta de perfil para un país.
 * Usa el nombre en idioma local del país (ej: "Deutschland" para DE, no "Germany").
 * Si el país no está mapeado, usa el texto genérico del idioma detectado.
 *
 * Ej:
 *   ("AR", "es-AR") → "Argentina"
 *   ("DE", "de-DE") → "Deutschland"
 *   ("FR", "fr-FR") → "France"
 *   ("ZZ", "en-US") → "International"   ← país desconocido
 */
export function getProfileLabelForCountry(
  country: string,
  language: string = "en-US",
): string {
  const label = COUNTRY_PROFILE_LABELS[country.toUpperCase()];
  if (label) return label;
  // Fallback: genérico en el idioma del usuario (viene de LOCALE_LABELS)
  return getLocaleLabels(language).profileLabel;
}

// ─── Playback IDs de Mux ──────────────────────────────────────────────────────

export interface BioTemplatePremiumPlaybackIds {
  standard?: string;
  vip?: string;
}

/** 
 * Extrae y valida el playback ID de Mux.
 * Si se pasa una URL completa (https://stream.mux.com/ID.m3u8), extrae solo el ID.
 * Valores cortos o placeholders se consideran inválidos.
 */
function validPlaybackId(id: string | undefined): string | undefined {
  if (!id || typeof id !== "string") return undefined;
  let t = id.trim();
  
  // Si es una URL de Mux, extraer el playback ID
  // Formato: https://stream.mux.com/PLAYBACK_ID.m3u8 o https://stream.mux.com/PLAYBACK_ID
  const muxUrlMatch = t.match(/stream\.mux\.com\/([^/.?]+)/);
  if (muxUrlMatch) {
    t = muxUrlMatch[1];
  }
  
  // Validar que no sea placeholder o demasiado corto
  if (t.length < 10 || /your-|tu-|example|xxx/i.test(t)) return undefined;
  return t;
}

/**
 * Special Offer activo (VITE_PREMIUM_SPECIAL_OFFER true / 1 / active).
 * Usar en páginas que montan BioTemplatePremium para misma regla que el template.
 */
export function isPremiumSpecialOfferActive(): boolean {
  const specialOfferEnv = (import.meta.env.VITE_PREMIUM_SPECIAL_OFFER as string)?.toLowerCase().trim();
  return specialOfferEnv === "true" || specialOfferEnv === "1" || specialOfferEnv === "active";
}

function isSpecialOfferActive(): boolean {
  return isPremiumSpecialOfferActive();
}

function maskMuxPlaybackIdForLog(id: string): string {
  if (id.length < 14) return "(short)";
  return `${id.slice(0, 8)}…${id.slice(-4)}`;
}

/** De dónde salió el playback VIP (solo diagnóstico; no PII). */
function describePremiumVipMuxSource(
  baseLang: string,
  vip: string | undefined,
  vipVideoId: string | undefined,
  esVip: string | undefined,
  enVip: string | undefined,
): string {
  if (!vip) return "MISSING";
  if (vipVideoId && vip === vipVideoId) return "VITE_SPECIAL_OFFER_PLAYBACK_ID";
  if (baseLang === "es") {
    if (esVip && vip === esVip) return "VITE_PREMIUM_MUX_ES_VIP";
    if (enVip && vip === enVip)
      return "VITE_PREMIUM_MUX_EN_VIP (cross-locale, base=es)";
  }
  if (baseLang === "en") {
    if (enVip && vip === enVip) return "VITE_PREMIUM_MUX_EN_VIP";
    if (esVip && vip === esVip)
      return "VITE_PREMIUM_MUX_ES_VIP (cross-locale, base=en)";
  }
  if (enVip && vip === enVip) return "VITE_PREMIUM_MUX_EN_VIP (fallback, other locale)";
  if (esVip && vip === esVip) return "VITE_PREMIUM_MUX_ES_VIP (fallback, other locale)";
  return "resolved";
}

let _premiumMuxVipLogDedup: { key: string; at: number } = { key: "", at: 0 };
function shouldLogPremiumMuxVipAgain(key: string, windowMs: number): boolean {
  const now = Date.now();
  if (key === _premiumMuxVipLogDedup.key && now - _premiumMuxVipLogDedup.at < windowMs)
    return false;
  _premiumMuxVipLogDedup = { key, at: now };
  return true;
}

/**
 * Retorna los playback IDs de Mux **VIP** para el idioma del usuario.
 * `MuxVideoPlayerWithCTA` solo recibe el playback VIP (`BioTemplatePremium`, `BioTemplatePremiumSimple`); el plan Standard no tiene reproductor Mux ahí.
 *
 * Si el Special Offer está activo, usa `VITE_SPECIAL_OFFER_PLAYBACK_ID` para el video VIP.
 *
 * **VIP:** solo variables `VITE_PREMIUM_MUX_*_VIP`. Por idioma primero; si falta la del locale, se usa la otra (ES ↔ EN). Sin ID en repo.
 */
export function getPlaybackIds(
  language: string,
): BioTemplatePremiumPlaybackIds {
  const lang = baseLang(language);
  
  // Special Offer: usar video específico de la promoción si está configurado
  const specialOfferPlaybackId = validPlaybackId(import.meta.env.VITE_SPECIAL_OFFER_PLAYBACK_ID as string | undefined);
  const useSpecialOfferVideo = isSpecialOfferActive() && specialOfferPlaybackId;

  const esVip = validPlaybackId(import.meta.env.VITE_PREMIUM_MUX_ES_VIP as string | undefined);
  const enVip = validPlaybackId(import.meta.env.VITE_PREMIUM_MUX_EN_VIP as string | undefined);

  // Si hay Special Offer activo, usar el video de la promoción para ambos idiomas
  const vipVideoId = useSpecialOfferVideo ? specialOfferPlaybackId : undefined;

  const ids: Record<string, BioTemplatePremiumPlaybackIds> = {
    es: {
      vip: vipVideoId || esVip || enVip,
    },
    en: {
      vip: vipVideoId || enVip || esVip,
    },
  };
  const resolved = ids[lang] ?? {
    vip: vipVideoId || enVip || esVip,
  };
  const vip = coalesceMuxPlaybackId(resolved.vip);
  const muxSource = describePremiumVipMuxSource(
    lang,
    vip,
    vipVideoId,
    esVip,
    enVip,
  );

  if (typeof window !== "undefined") {
    if (!vip) {
      console.warn(
        "[premium-mux-vip] Missing VIP playback — premium funnel Mux will not load.",
        {
          locale: language,
          base_lang: lang,
          special_offer_active: Boolean(useSpecialOfferVideo),
          special_offer_playback_valid: Boolean(specialOfferPlaybackId),
          raw_es_present: Boolean(
            (import.meta.env.VITE_PREMIUM_MUX_ES_VIP as string | undefined)?.trim(),
          ),
          raw_en_present: Boolean(
            (import.meta.env.VITE_PREMIUM_MUX_EN_VIP as string | undefined)?.trim(),
          ),
          after_validation_es: Boolean(esVip),
          after_validation_en: Boolean(enVip),
          hint:
            "Set VITE_PREMIUM_MUX_ES_VIP and VITE_PREMIUM_MUX_EN_VIP in Vercel → redeploy. Values must pass validation (length ≥10, not placeholders). If Special Offer is on, set VITE_SPECIAL_OFFER_PLAYBACK_ID too.",
        },
      );
    } else if (
      shouldLogPremiumMuxVipAgain(`ok:${lang}:${vip.slice(0, 12)}`, 5000)
    ) {
      console.info("[premium-mux-vip]", {
        locale: language,
        base_lang: lang,
        mux_source: muxSource,
        playback_id: maskMuxPlaybackIdForLog(vip),
      });
    }
  }

  return { vip };
}

/**
 * Retorna los planes (standard + vip) según la moneda,
 * con textos localizados al idioma del usuario.
 * VIP incluye el playbackId del video para ese idioma: env vars ES/EN VIP (sin fallback en código).
 * Siempre retorna vipPlan, pero marca isDisabled cuando no hay capacidad.
 */
export function getPlansForCurrency(
  currency: "ARS" | "USD" | "EUR",
  language: string = "en-US",
): {
  standardPlan: BioTemplatePremiumPlan;
  vipPlan: BioTemplatePremiumVipPlan & { isDisabled?: boolean };
} {
  const labels = getLocaleLabels(language);
  const playbackIds = getPlaybackIds(language);
  const vipEnabled = isVipPlanCardAvailable();

  if (currency === "ARS") {
    return {
      standardPlan: {
        price: PRICES.ars.standard,
        productName: labels.standardName,
        subtitle: labels.standardSubtitle,
      },
      vipPlan: {
        price: PRICES.ars.vip,
        productName: labels.vipName,
        subtitle: labels.vipSubtitle,
        playbackId: playbackIds.vip,
        isDisabled: !vipEnabled,
      },
    };
  }

  const row = currency === "EUR" ? PRICES.eur : PRICES.usd;

  return {
    standardPlan: {
      price: row.standard,
      productName: labels.standardName,
      subtitle: labels.standardSubtitle,
    },
    vipPlan: {
      price: row.vip,
      productName: labels.vipName,
      subtitle: labels.vipSubtitle,
      playbackId: playbackIds.vip,
      isDisabled: !vipEnabled,
    },
  };
}

// ─── Analytics: snapshot estable para PostHog + Slack ─────────────────────────

/** Datos serializables alineados con `getPlansForCurrency` / env (sin PII). */
export interface PremiumPlansFunnelSnapshot {
  /** Versión del shape del snapshot (migración dashboards / HogQL). */
  snapshot_version: number;
  currency: "ARS" | "USD" | "EUR";
  language_base: string;
  standard_price: number;
  vip_price: number;
  vip_plan_card_enabled: boolean;
  special_offer_active: boolean;
  vip_discount_rate: number;
  quarterly_discount_rate: number;
  /** Legado: no hay Mux Standard en templates premium; usar `has_vip_mux`. */
  has_standard_mux: boolean;
  /** Indica si hay playback VIP listo (único Mux del funnel en `MuxVideoPlayerWithCTA`). */
  has_vip_mux: boolean;
}

/** Incrementar solo si cambian campos del snapshot (breaking para insights guardados). */
export const PREMIUM_FUNNEL_SNAPSHOT_VERSION = 2 as const;

function readDiscountRateEnv(
  key: "VITE_PREMIUM_VIP_DISCOUNT_RATE" | "VITE_PREMIUM_QUARTERLY_DISCOUNT_RATE",
  fallback: number,
): number {
  const raw = String(import.meta.env[key] ?? "").trim();
  const n = raw ? parseFloat(raw) : NaN;
  return Number.isFinite(n) && n >= 0 ? n : fallback;
}

/**
 * Contexto único de precios/feature flags para correlacionar PostHog ↔ Slack ↔ `flow_trace_id`.
 * Usar en el inicio de sesión del funnel premium (BioTemplatePremium / Simple).
 */
export function getPremiumPlansFunnelSnapshot(
  currency: "ARS" | "USD" | "EUR",
  language: string,
): PremiumPlansFunnelSnapshot {
  const lang = baseLang(language);
  const row =
    currency === "ARS" ? PRICES.ars : currency === "EUR" ? PRICES.eur : PRICES.usd;
  const playbackIds = getPlaybackIds(language);
  const vipEnabled = isVipPlanCardAvailable();
  return {
    snapshot_version: PREMIUM_FUNNEL_SNAPSHOT_VERSION,
    currency,
    language_base: lang,
    standard_price: row.standard,
    vip_price: row.vip,
    vip_plan_card_enabled: vipEnabled,
    special_offer_active: isPremiumSpecialOfferActive(),
    vip_discount_rate: readDiscountRateEnv("VITE_PREMIUM_VIP_DISCOUNT_RATE", 0.1),
    quarterly_discount_rate: readDiscountRateEnv(
      "VITE_PREMIUM_QUARTERLY_DISCOUNT_RATE",
      0.15,
    ),
    /** Siempre false: el plan Standard no usa Mux en `BioTemplatePremium` / `BioTemplatePremiumSimple` (solo VIP → `MuxVideoPlayerWithCTA`). Conservado para compatibilidad en PostHog. */
    has_standard_mux: false,
    /** Principal: playback VIP resuelto (env + fallback); alinea con el único Mux del funnel. */
    has_vip_mux: Boolean(playbackIds.vip),
  };
}
